import * as R from 'ramda'
import { stripOutHtml } from '../helpers'
import { getQuestionTitle } from '../../utils/QuestionHelper'

class DataFormatter {
    constructor(appStore, productNames, portfolioNames, divisionNames, dashStore, tagNames) {
        this.appStore = appStore
        this.productNames = productNames
        this.portfolioNames = portfolioNames
        this.divisionNames = divisionNames
        this.dashStore = dashStore
        this.tagNames = tagNames
    }

    sortBy = (arr, key, direction = 1) => {
        if (!key || key === 'none') return arr
        return arr.sort((a, b) => {
            let value = 0
            const valueA = !isNaN(Number(a[key])) ? Number(a[key]) : a[key]
            const valueB = !isNaN(Number(b[key])) ? Number(b[key]) : b[key]
            if (valueA < valueB) {
                value = -1
            } else if (valueA > valueB) {
                value = 1
            }
            return value * direction
        })
    }

    convertCurrency = (value, exchangeRateDate, element) => {
        const { currencyConversions, todaysCurrencyRates } = this.dashStore
        const decimals = R.pathOr(element.chartType === 'gauge0-5' ? 2 : 0, ['widgetRenderSettings', 'dataDecimals'], element)
        const setDecimals = ['text', 'multiText', 'gauge', 'gauge0-5', 'lmx'].includes(element.chartType)
        const widgetCurrency = R.pathOr(element.isCurrency ? 'USD' : undefined, ['widgetRenderSettings', 'currency'], element)
        if (!widgetCurrency) return setDecimals && typeof value === 'number' ? value.toFixed(decimals) : value
        const exchangeRate = R.pathOr(
            R.pathOr(1, [widgetCurrency], todaysCurrencyRates),
            [exchangeRateDate, widgetCurrency],
            currencyConversions,
        )
        return setDecimals ? (value * exchangeRate).toFixed(decimals) : value * exchangeRate
    }

    removeProductTag = (text) => {
        const textArr = text.split('-')
        textArr.pop()
        return textArr.join('-')
    }

    getItemName = (element, portfolioId, productId, divisionId, year, multiName, tagId) => {
        const {
            productNames, portfolioNames, divisionNames, tagNames,
        } = this
        const { appStore } = this
        let name = ''
        if (element.scope === 'client') name = appStore.userData.clientName
        else if (element.scope === 'portfolio') name = portfolioNames[portfolioId]
        else if (element.scope === 'product') name = productNames[productId]
        else if (element.scope === 'division') name = divisionNames[divisionId]
        const yearName = year && year >= 1900 ? ` - ${year}` : ''
        const multi = multiName ? ` - ${multiName}` : ''
        const tag = tagId ? ` - ${tagNames[tagId]}` : ''
        return `${name}${yearName}${multi}${tag}`
            .replace('&amp;', '')
    }

    formatYearName = (year) => {
        if (year >= 1900) return year
        if (Number(year) === 0 || Number(year) === 84) return 'All Time'
        return `${year} Months`
    }

    formatAvgData = (unsortedData, element) => {
        const data = unsortedData.sort((a, b) => a.year - b.year)
        if (['multiQuestion', 'quadrantAnalysis'].includes(element.kpiType) && element.chartType !== 'multiText') {
            const yData = data.reduce((obj, d) => {
                const value = obj[d.multiName || element.title] || []
                value.push({
                    name: `${d.divisionId}_${d.portfolioId}_${d.productId}${d.multiName ? `_${d.multiName}` : ''}`,
                    division: d.divisionId,
                    portfolio: d.portfolioId,
                    product: d.productId,
                    multiName: d.multiName,
                    value: this.convertCurrency(R.pathOr(d.data, ['data', d.multiQuestionId], d), d.exchangeRateDate, element),
                    year: d.year,
                    endDate: d.endDate,
                    benchmarkName: d.benchmarkName,
                    benchmark: d.benchmark,
                    tagOptionId: d.tagOptionId,
                })
                return { ...obj, [d.multiName || element.title]: value }
            }, {})

            return Object.keys(yData).map((y) => ({
                data: yData[y].map((v) => ({
                    value: Number(v.value),
                    name: v.benchmarkName || this.getItemName(element, v.portfolio, v.product, v.division, v.year, undefined, v.tagOptionId),
                    endDate: v.endDate,
                    benchmark: v.benchmark,
                    benchmarkName: v.benchmarkName,
                    productId: v.product,
                    portfolioId: v.portfolio,
                    divisionId: v.division,
                    tagOptionId: v.tagOptionId,
                })),
                name: y || element.title,
            }))
        }
        if (['YOY', '', 'ROL', 'ROL-36', 'ROL-84', 'ALL'].includes(element.benchmarkType) && ['bar', 'column', 'line'].includes(element.chartType)) {
            const yData = data.reduce((obj, d) => {
                const value = obj[d.year] || []
                value.push({
                    name: `${d.divisionId}_${d.portfolioId}_${d.productId}${d.multiName ? `_${d.multiName}` : ''}`,
                    division: d.divisionId,
                    portfolio: d.portfolioId,
                    product: d.productId,
                    multiName: d.multiName,
                    value: this.convertCurrency(
                        R.pathOr(d.data, ['data', d.multiQuestionId], d),
                        d.exchangeRateDate,
                        element,
                    ),
                    endDate: d.endDate,
                    benchmarkName: d.benchmarkName,
                    benchmark: d.benchmark,
                    productId: d.productId,
                    tagOptionId: d.tagOptionId,
                })
                return { ...obj, [d.year]: value }
            }, {})

            return Object.keys(yData).map((y) => ({
                data: yData[y].map((v) => ({
                    value: Number(v.value),
                    name: v.benchmarkName || this.getItemName(element, v.portfolio, v.product, v.division, undefined, v.multiName, v.tagOptionId),
                    year: v.year,
                    endDate: v.endDate,
                    benchmark: v.benchmark,
                    benchmarkName: v.benchmarkName,
                    productId: v.product,
                    tagOptionId: v.tagOptionId,
                })),
                name: this.formatYearName(y),
            }))
        }

        return data.map((d) => ({
            value: Number(this.convertCurrency(R.pathOr(d.data, ['data', d.multiQuestionId], d), d.exchangeRateDate, element)),
            name: d.benchmarkName || this.getItemName(
                element, d.portfolioId, d.productId, d.divisionId, d.year, d.multiName, d.tagOptionId,
            ),
            year: d.year,
            endDate: d.endDate,
            benchmarkName: d.benchmarkName,
            benchmark: d.benchmark,
            productId: d.productId,
            tagOptionId: d.tagOptionId,
        }))
    }

    formatPctData = (unsortedData, element) => {
        const data = unsortedData.sort((a, b) => a.year - b.year)
        const { options } = element
        const optionFilters = R.pathOr([], ['optionFilter'], element).map((f) => f.value)
        const filteredOptions = optionFilters.length
            ? options.filter((o) => optionFilters.includes(o[0]))
            : options
        const names = filteredOptions.reduce((n, c) => ({ ...n, [R.pathOr('', [0], c)]: stripOutHtml(R.pathOr('', [1], c)) }), {})
        return data.map((d) => ({
            data: filteredOptions.map(
                ([k]) => ({
                    value: Number(this.convertCurrency(
                        d.data[k], d.exchangeRateDate, element,
                    )),
                    name: names[k],
                    tagOptionId: d.tagOptionId,
                }),
            ),
            name: d.benchmarkName || this.getItemName(
                element, d.portfolioId, d.productId, d.divisionId, d.year, d.multiName, d.tagOptionId,
            ),
            year: d.year,
            endDate: d.endDate,
            benchmarkName: d.benchmarkName,
            benchmark: d.benchmark,
            productId: d.productId,
            tagOptionId: d.tagOptionId,
            tagOptionName: d.tagOptionId ? this.tagNames[d.tagOptionId] : undefined,
        }))
    }

    formatHeatData = (unsortedData, element) => {
        const data = unsortedData.sort((a, b) => a.year - b.year)
        const { options } = element
        return data.map((d) => ({
            options,
            data: d.data.slice().map((d) => d.slice()),
            surveyDataIds: Object.fromEntries(Object.entries(d.surveyDataIds ?? {}).map(([k, v]) => [k, v.slice()])),
            year: d.year,
            endDate: d.endDate,
            benchmarkName: d.benchmarkName,
            benchmark: d.benchmark,
            productId: d.productId,
            tagOptionId: d.tagOptionId,
            tagOptionName: d.tagOptionId ? this.tagNames[d.tagOptionId] : undefined,
        }))
    }

    formatCIMV = (unsortedData, element) => {
        const data = unsortedData.sort((a, b) => a.year - b.year)
        const { options } = element

        return data.map((d) => ({
            options,
            data: d.data,
            year: d.year,
            endDate: d.endDate,
            benchmarkName: d.benchmarkName,
            benchmark: d.benchmark,
            productId: d.productId,
            tagOptionId: d.tagOptionId,
            tagOptionName: d.tagOptionId ? this.tagNames[d.tagOptionId] : undefined,
        }))
    }

    formatNFOption = (unsortedData, element) => {
        const data = unsortedData.sort((a, b) => a.year - b.year)
        const names = element.options.reduce((n, c) => (
            c[0] === element.questionId ? { ...n, [c[0]]: stripOutHtml(c[1]) } : n
        ), {})

        if (['YOY', '', 'ROL', 'ROL-36', 'ROL-84', 'ALL'].includes(element.benchmarkType) && ['bar', 'column', 'line'].includes(element.chartType)) {
            return data.map((d) => ({
                data: [{
                    value: Number(this.convertCurrency(
                        d.data[element.questionId],
                        d.exchangeRateDate,
                        element,
                    )),
                    name: names[element.questionId],
                }],
                name: d.benchmarkName || this.getItemName(
                    element, d.portfolioId, d.productId, d.divisionId, d.year, d.multiName, d.tagOptionId,
                ),
                year: d.year,
                benchmarkName: d.benchmarkName,
                benchmark: d.benchmark,
                productId: d.productId,
                tagOptionId: d.tagOptionId,
            }))
        }
        return data.map((d) => ({
            value: Number(this.convertCurrency(
                d.data[element.questionId],
                d.exchangeRateDate,
                element,
            )),
            name: d.benchmarkName || this.getItemName(
                element, d.portfolioId, d.productId, d.divisionId, d.year, d.multiName, d.tagOptionId,
            ),
            year: d.year,
            benchmarkName: d.benchmarkName,
            benchmark: d.benchmark,
            productId: d.productId,
            tagOptionId: d.tagOptionId,
        }))
    }

    getTBOptionName = (o, element) => {
        const { langcode } = this.appStore
        return R.pathOr('', ['text'], getQuestionTitle(
            {
                content: R.pathOr({}, ['langs'], o),
                locale: langcode,
                defaultLocale: element.questionInitialLocale,
                initialLocale: 'en_GB',
            },
        ))
    }

    formatTBRowData = (data, element) => {
        const { questionId, benchmarkType, chartType } = element

        // Format series as years (one series per year)
        if (['YOY', '', 'ROL', 'ROL-36', 'ROL-84', 'ALL'].includes(benchmarkType) && ['bar', 'column', 'line'].includes(chartType)) {
            const yData = data.reduce((obj, d) => {
                const value = obj[d.year] || []
                value.push({
                    value: Number(this.convertCurrency(
                        R.pathOr(d.data, ['data', d.multiQuestionId || questionId], d),
                        d.exchangeRateDate,
                        element,
                    )),
                    name: d.benchmarkName
                        || this.getItemName(element, d.portfolioId, d.productId, d.divisionId, undefined, d.multiName, d.tagOptionId),
                    endDate: d.endDate,
                    productId: d.productId,
                    tagOptionId: d.tagOptionId,
                    benchmark: d.benchmark,
                    benchmarkName: d.benchmarkName,
                })
                return { ...obj, [d.year]: value }
            }, {})

            return Object.keys(yData).map((y) => ({
                data: yData[y],
                name: this.formatYearName(y),
            }))
        }

        // Format series as a single series for other benchmarkTypes (e.g. last-24)
        return [{
            name: element.title,
            data: data.map((d) => ({
                value: Number(this.convertCurrency(
                    R.pathOr(d.data, ['data', d.multiQuestionId || questionId], d),
                    d.exchangeRateDate,
                    element,
                )),
                name: d.benchmarkName || this.getItemName(
                    element, d.portfolioId, d.productId, d.divisionId, d.year, d.multiName, d.tagOptionId,
                ),
                year: d.year,
                endDate: d.endDate,
                benchmarkName: d.benchmarkName,
                benchmark: d.benchmark,
                productId: d.productId,
                tagOptionId: d.tagOptionId,
            })),
        }]
    }

    formatTBData = (unsortedData, element) => {
        const { questionId, kpiType, chartType } = element
        const data = unsortedData.sort((a, b) => a.year - b.year)
        const tbColumns = R.pathOr([], ['columns'], element)

        const optionFilters = R.pathOr([], ['optionFilter'], element).map((f) => f.value)
        const filteredTbColumns = optionFilters.length
            ? tbColumns.filter((o) => o !== null && optionFilters.includes(o[0]))
            : tbColumns

        const names = element.questionOptions.reduce((obj, o) => (
            { ...obj, [o.id]: stripOutHtml(this.getTBOptionName(o, element)) }
        ), {})
        const options = element.questionTypeCode === 'TB - row' ? { [questionId]: names[questionId] } : names
        if (['donut', 'pie', 'treemap'].includes(chartType)) {
            return filteredTbColumns.map(([cId, cName]) => ({
                name: cName,
                value: this.convertCurrency(
                    data?.[0]?.data[`${questionId}_${cId}`],
                    data?.[0]?.data.exchangeRateDate,
                    element,
                ),
            }))
        }

        if (element.questionTypeCode === 'TB - row' && kpiType === 'avg') {
            return this.formatTBRowData(data, element)
        }

        if (kpiType === 'avg') {
            return data.map((d) => ({
                data: Object.keys(options).map((optionId) => ({
                    name: `${names[optionId]}`,
                    value: Number(this.convertCurrency(d.data[optionId], d.exchangeRateDate, element)),
                    year: d.year,
                    endDate: d.endDate,
                })),
                name: d.benchmarkName || this.getItemName(
                    element, d.portfolioId, d.productId, d.divisionId, d.year, d.multiName, d.tagOptionId,
                ),
                benchmarkName: d.benchmarkName,
                benchmark: d.benchmark,
                productId: d.productId,
            }))
        }

        // other kpiTypes (e.g. 'pct')
        return filteredTbColumns.map(([cId, cName]) => ({
            name: cName,
            data: data.map((d) => Object.keys(options).map((optionId) => ({
                name: d.benchmarkName || `${this.getItemName(
                    element, d.portfolioId, d.productId, d.divisionId, d.year, d.multiName,
                )} - ${names[optionId]}`,
                value: this.convertCurrency(
                    d.data[`${optionId}_${cId}`],
                    d.exchangeRateDate,
                    element,
                ),
                year: d.year,
                endDate: d.endDate,
                benchmarkName: d.benchmarkName,
                benchmark: d.benchmark,
                productId: d.productId,
            }))).flat(1),
        }))
    }

    groupXAxisLabels = (labels, removeZeroNull, element) => Object.values(labels.reduce((obj, l) => {
        const oldValue = !isNaN(Number(R.pathOr(0, [l.name, 'value'], obj))) ? Number(R.pathOr(0, [l.name, 'value'], obj)) : 0
        const newValue = R.pathOr([{ value: l.value }], ['data'], l).reduce((total, v) => total + Number(v.value), oldValue)
        return {
            ...obj,
            [`${l.name}-${l.productId}`]: {
                name: `${l.name}-${element.scope === 'product' && l.productId ? l.productId : ''}`,
                value: !isNaN(newValue) ? newValue : 0,
                endDate: new Date(l.endDate) > R.pathOr(0, [l.name, 'endDate'], obj)
                    ? new Date(l.endDate)
                    : R.pathOr(0, [l.name, 'endDate'], obj),
                year: l.year,
                isNull: isNaN(newValue),
                tagOptionName: l.tagOptionId ? this.tagNames[l.tagOptionId] : undefined,
            },
        }
    }, {})).filter((d) => (
        (!['zero', 'both', 'null'].includes(removeZeroNull))
            || (removeZeroNull === 'zero' && !(d.value === 0 && !d.isNull))
            || (removeZeroNull === 'null' && !d.isNull)
            || (removeZeroNull === 'both' && !d.isNull && d.value !== 0)
    ))

    getXAxisLabels = (element, fData) => {
        const {
            chartType, multiQuestion, dataDetails, kpiType,
        } = element
        const { type } = dataDetails
        const { oId } = this.dashStore
        const { sortBy, groupXAxisLabels } = this

        const defaultSortKey = oId !== 6 && element.scope === 'product' ? 'endDate' : 'none'
        const sortByKey = R.pathOr(defaultSortKey, ['widgetRenderSettings', 'sortBy'], element)
        const sortDirection = R.pathOr(1, ['widgetRenderSettings', 'sortDirection'], element)

        const removeZeroNull = R.pathOr(undefined, ['widgetRenderSettings', 'removeZeroNull'], element)

        if (chartType === 'quadrantAnalysis') {
            return {
                x: `${R.pathOr(undefined, [0, 'label'], multiQuestion)} - ${R.pathOr('', [0, 'group'], multiQuestion)}`,
                y: `${R.pathOr(undefined, [1, 'label'], multiQuestion)} - ${R.pathOr('', [1, 'group'], multiQuestion)}`,
                z: `${R.pathOr(undefined, [2, 'label'], multiQuestion)} - ${R.pathOr('', [2, 'group'], multiQuestion)}`,
            }
        }

        const formatAsRDQuestion = ['TB', 'TB - row'].includes(element.questionTypeCode)

        if ((['column', 'line', 'bar'].includes(chartType) && ['YOY', '', 'ROL', 'ROL-36', 'ROL-84', 'ALL'].includes(element.benchmarkType))
        || (['multiQuestion', 'quadrantAnalysis'].includes(kpiType) && element.chartType !== 'multiText')
        || formatAsRDQuestion) {
            return !['pct', 'quadrantAnalysis'].includes(type)
                ? [...new Set(
                    sortBy(
                        groupXAxisLabels(fData
                            .filter((d) => Array.isArray(d.data)) // skip when fData is not yet ready (todo: review reactivity order)
                            .map(((d) => d.data.map((nd) => nd)))
                            .flat(2), removeZeroNull, element),
                        sortByKey,
                        sortDirection,
                    )
                        .map((d) => d.name),
                )]
                : sortBy(groupXAxisLabels(R.pathOr(fData, [0, 'data'], fData), removeZeroNull, element), sortByKey, sortDirection).map((d) => d.name)
        }

        if (['stackedColumn', 'stackedLine', 'stackedBar'].includes(chartType)
         || (['column', 'line', 'bar'].includes(chartType) && !['YOY', '', 'ROL', 'ROL-36', 'ROL-84', 'ALL'].includes(element.benchmarkType))) {
            return sortBy(groupXAxisLabels(fData, removeZeroNull, element), sortByKey, sortDirection).map((d) => d.name)
        }
        return []
    }

    formatBarColumnData = (fData, xAxisLabels, element) => {
        const { benchmarkType, questionTypeCode, dataDetails } = element
        const { type } = dataDetails
        const formatAsRDQuestion = ['TB', 'TB - row'].includes(questionTypeCode)

        if (formatAsRDQuestion) {
            return fData.map((s) => {
                const optionData = s.data.reduce((obj, o) => ({ ...obj, [`${o.name}-${element.scope === 'product' && o.productId ? o.productId : ''}`]: o }), {})
                return { ...s, data: xAxisLabels.map((l) => optionData[l]) }
            })
        }
        const skipBenchmarkTypes = !['YOY', '', 'ROL', 'ROL-36', 'ROL-84', 'ALL'].includes(benchmarkType)
        const isMultiMetric = ['multiQuestion', 'quadrantAnalysis'].includes(element.kpiType)

        if (skipBenchmarkTypes && !isMultiMetric) {
            const labelData = fData
                .reduce((obj, v) => ({ ...obj, [`${v.name}-${element.scope === 'product' && v.productId ? v.productId : ''}`]: v }), {})
            return ['pct'].includes(type)
                ? R.pathOr([], [0, 'data'], fData).map((c, i) => (
                    {
                        data: xAxisLabels.map((d) => ({ ...labelData[d].data[i], name: d.name })),
                        name: c.name,
                    }
                ))
                : [{
                    name: element.title,
                    data: xAxisLabels.map((d) => ({
                        value: labelData[d].value,
                        benchmark: labelData[d].benchmark,
                        benchmarkName: labelData[d].benchmark,
                        name: labelData[d].name,
                        tagOptionId: labelData[d].tagOptionId,
                        tagOptionName: labelData[d].tagOptionId ? this.tagNames[labelData[d].tagOptionId] : undefined,
                    })),
                }]
        }
        return fData.map((d) => {
            const labelData = d.data
                .reduce((obj, v) => ({ ...obj, [`${v.name}-${element.scope === 'product' && v.productId ? v.productId : ''}`]: v }), {})

            return {
                data: xAxisLabels.map((l) => labelData[l] || { value: null }),
                name: d.name,
                benchmarkName: d.benchmarkName,
                benchmark: d.benchmark,
                tagOptionId: d.tagOptionId,
                tagOptionName: d.tagOptionId ? this.tagNames[d.tagOptionId] : undefined,
            }
        })
    }

    formatGauge = (fData) => (
        fData.map((data) => R.mergeLeft(data, R.pathOr({}, ['data', 0], data)))
    )

    formatStackedData = (fData, element, xAxisLabels) => {
        const { questionTypeCode, dataDetails } = element
        const { type } = dataDetails
        if (['multiQuestion', 'quadrantAnalysis'].includes(element.kpiType)) {
            return fData.map((d) => {
                const labelData = d.data
                    .reduce((obj, v) => ({ ...obj, [`${v.name}-${element.scope === 'product' && v.productId ? v.productId : ''}`]: v }), {})
                return {
                    data: xAxisLabels.map((l) => labelData[l] || 0),
                    name: d.name,
                }
            })
        }

        if (['TB', 'TB - row'].includes(questionTypeCode)) {
            return fData.map((s) => {
                const optionData = s.data.reduce((obj, o) => ({ ...obj, [`${o.name}-${element.scope === 'product' && o.productId ? o.productId : ''}`]: o }), {})
                return { ...s, data: xAxisLabels.map((l) => optionData[l]) }
            })
        }

        const labelData = fData
            .reduce((obj, v) => ({ ...obj, [`${v.name}-${element.scope === 'product' && v.productId ? v.productId : ''}`]: v }), {})
        return ['pct'].includes(type)
            ? R.pathOr([], [0, 'data'], fData).map((c, i) => (
                {
                    data: xAxisLabels.map((d) => ({ ...labelData[d].data[i], name: d.name })),
                    name: c.name,
                }
            ))
            : [{ name: element.title, data: xAxisLabels.map((d) => labelData[d].value) }]
    }

    getQuadrantSeriesName = (seriesName, records, index) => {
        switch (seriesName) {
            case 'division': {
                return String(R.pathOr('undefined', [R.pathOr(undefined, ['xAxis', index, 'divisionId'], records)], this.divisionNames))
            }
            case 'portfolio': {
                return String(R.pathOr('undefined', [R.pathOr(undefined, ['xAxis', index, 'portfolioId'], records)], this.portfolioNames))
            }
            case 'product': {
                return String(R.pathOr('undefined', [R.pathOr(undefined, ['xAxis', index, 'productId'], records)], this.productNames))
            }
            default: {
                return String(R.pathOr(undefined, ['xAxis', index, 'year'], records))
            }
        }
    }

    convertQuadrantCurrency = (element, value, axis, exchangeRateDate) => (
        R.pathOr(false, ['quadrantCurrency', axis], element)
            ? this.convertCurrency(value, exchangeRateDate, element)
            : value
    )

    formatQuadrantAnalysis = (fData, element) => {
        const groupedData = fData.reduce((gd, d) => {
            const curAxisVal = gd[d.multiName] || {}
            return {
                ...gd,
                [d.multiName]: {
                    ...curAxisVal,
                    [this.getItemName(
                        element, d.portfolioId, d.productId, d.divisionId, d.year >= 1900 ? d.year : new Date(d.endDate).getFullYear(), undefined, d.tagOptionId,
                    )]: {
                        ...d,
                        data: typeof d.data !== 'object'
                            ? this.convertQuadrantCurrency(element, d.data, d.multiName, d.exchangeRateDate)
                            : null,
                    },
                },
            }
        }, {})

        return Object.keys(groupedData.xAxis || {}).map((x) => ({
            x: R.pathOr(null, ['xAxis', x, 'data'], groupedData),
            y: R.pathOr(null, ['yAxis', x, 'data'], groupedData),
            z: R.pathOr(null, ['zAxis', x, 'data'], groupedData),
            name: x,
            series: this.getQuadrantSeriesName(
                R.pathOr('year', ['widgetRenderSettings', 'colouredBy'], element),
                groupedData, x,
            ),
        }))
    }

    swapSeriesXAxisData = (unSortedData, xAxis, element) => {
        const { oId } = this.dashStore
        const { sortBy } = this

        const defaultSortKey = oId !== 6 && element.scope === 'product' ? 'endDate' : 'none'
        const sortByKey = R.pathOr(defaultSortKey, ['widgetRenderSettings', 'sortBy'], element)
        const sortDirection = R.pathOr(1, ['widgetRenderSettings', 'sortDirection'], element)
        const data = sortBy(unSortedData, sortByKey, sortDirection)
        const xAxisLabels = data.map((d) => d.name)
        const fData = xAxis.map((x, i) => {
            const newSeries = {
                name: this.removeProductTag(x),
                data: [],
            }
            data.forEach((s) => {
                newSeries.data.push(R.pathOr(s.data, ['data', i], s))
            })
            return newSeries
        })
        return { fData, xAxisLabels }
    }

    formatData = (filteredData, element) => {
        const { kpiType, dataDetails, chartType } = element
        const type = dataDetails?.type
        let fData
        // format incoming data
        if (['TX', 'TA'].includes(element.questionTypeCode)) return { fData: filteredData }
        if (element.nonQuestionType === 'KPI') {
            if (['pct'].includes(type)) fData = this.formatPctData(filteredData, element)
            else fData = this.formatAvgData(filteredData, element)
        } else if (['heat'].includes(kpiType) || type === 'heat') fData = this.formatHeatData(filteredData, element)
        else if (element.parentQuestion && !['TB', 'TB - row'].includes(element.questionTypeCode)) fData = this.formatNFOption(filteredData, element)
        else if (['TB', 'TB - row'].includes(element.questionTypeCode) && kpiType !== 'multiQuestion') fData = this.formatTBData(filteredData, element)
        else if ((['avg', 'nps'].includes(kpiType) || element.nonQuestionType) && type !== 'LMAT') fData = this.formatAvgData(filteredData, element)
        else if (['pct'].includes(kpiType) || type === 'LMAT') fData = this.formatPctData(filteredData, element)

        if (!fData || R.isEmpty(fData)) return [] // avoid formating errors if there is no fData at this point
        const xAxisLabels = this.getXAxisLabels(element, fData)

        // format for specific chart
        if (['column', 'bar', 'line'].includes(element.chartType)) fData = this.formatBarColumnData(fData, xAxisLabels, element)
        else if (['quadrantAnalysis'].includes(element.chartType)) fData = this.formatQuadrantAnalysis(filteredData, element)
        else if (['stackedColumn', 'stackedBar', 'stackedLine'].includes(element.chartType)) fData = this.formatStackedData(fData, element, xAxisLabels)
        else if (['gauge', 'text'].includes(element.chartType) && element.questionTypeCode === 'TB - row') fData = this.formatGauge(fData, element)

        const swapSeriesXAxis = R.pathOr(false, ['widgetRenderSettings', 'swapSeriesXAxis'], element)
        if (swapSeriesXAxis) return this.swapSeriesXAxisData(fData, xAxisLabels, element)

        return {
            fData: fData || [],
            xAxisLabels: Array.isArray(xAxisLabels)
                ? xAxisLabels.map((x) => this.removeProductTag(x))
                : xAxisLabels,
        }
    }
}

export default DataFormatter
